import React from "react";
import { Link } from "react-router-dom";
import { ArticleLayout } from "./ArticleLayout";

const GuiaTratamientosPage = () => {
  return (
    <ArticleLayout
      title="Tipos de tratamientos en centros de rehabilitación en Chile"
      description="Guía completa para elegir el tratamiento de adicciones más adecuado. Conoce los tipos, costos, duraciones y cómo acceder en Chile."
      heroImage="https://images.unsplash.com/photo-1559757175-5700dde675bc?w=1200&q=80"
      readTime="15 min"
      date="2026"
      categories={["Guía", "Tratamientos", "Centros de Rehabilitación"]}
      prevArticle={{ path: "/tratamiento-cognitivo-conductual", title: "Tratamiento Cognitivo-Conductual" }}
      nextArticle={{ path: "/centros-rehabilitacion-gratis", title: "Centros de Rehabilitación Gratis" }}
    >
      <h2 className="text-emerald-400">Guía Completa: Todo lo que Debes Saber sobre Centros de Rehabilitación en Chile</h2>
      <p>
        ¡Bienvenido a la guía definitiva para entender y elegir un centro de rehabilitación en Chile! Aquí encontrarás información real, basada en experiencias de miles de chilenos que han superado adicciones a drogas, alcohol, ludopatía o conductuales. El objetivo: que tomes una decisión informada y <strong>busques el centro perfecto en nuestro directorio exclusivo</strong>.
      </p>
      <p>
        Recuerda: la recuperación es posible (¡70-80% éxito con el tratamiento adecuado!). No lo hagas solo — <Link to="/centros-de-rehabilitacion-drogas-chile" className="text-emerald-400 hover:underline">explora centros ahora en nuestro directorio</Link> o <Link to="/asesoria" className="text-emerald-400 hover:underline">solicita asesoría experta</Link>.
      </p>

      <hr className="border-slate-700 my-8" />

      <h2 className="text-emerald-400">1. Tipos de Tratamientos Disponibles en Chile</h2>
      <p>
        En Chile, los centros ofrecen enfoques probados internacionalmente, adaptados a nuestra realidad. Elige según tu nivel de dependencia (usa nuestros <Link to="/herramientas" className="text-emerald-400 hover:underline">tests URICA/DAST</Link> para saberlo).
      </p>

      <h3 className="text-white">Ambulatorio</h3>
      <p>Vas diario o varias veces/semana (ej. 9-14 hrs). Ideal para moderado/bajo riesgo, compatible con trabajo/familia.</p>
      <ul>
        <li><strong>Enfoque:</strong> Terapias individuales/grupales, TCC (cognitivo-conductual).</li>
        <li><strong>Duración:</strong> 3-12 meses (promedio 6).</li>
      </ul>

      <h3 className="text-white">Residencial Básico (Cognitivo-Conductual)</h3>
      <p>Internación full-time. Estructura diaria estricta.</p>
      <ul>
        <li><strong>Enfoque:</strong> Desintoxicación + reestructuración hábitos.</li>
        <li><strong>Duración:</strong> 8-12 meses (pases cada 15 días después de mes 1).</li>
      </ul>

      <h3 className="text-white">Residencial Integrativo/Holístico</h3>
      <p>Combina mente-cuerpo-espíritu (yoga, mindfulness, nutrición).</p>
      <ul>
        <li><strong>Enfoque:</strong> TCC + terapias alternativas.</li>
        <li><strong>Duración:</strong> 9-10 meses o más.</li>
      </ul>

      <h3 className="text-white">Residencial Premium</h3>
      <p>Lujo total (habitaciones privadas, gimnasio, terapia equina).</p>
      <ul>
        <li><strong>Enfoque:</strong> Personalizado + comodidades VIP.</li>
        <li><strong>Duración:</strong> 6-12 meses.</li>
      </ul>

      <h3 className="text-white">Online</h3>
      <p>Videollamadas + apps. Perfecto para privacidad o regiones remotas.</p>
      <ul>
        <li><strong>Enfoque:</strong> TCC virtual + seguimiento chat.</li>
        <li><strong>Duración:</strong> 3-12 meses (más corto/flexible).</li>
      </ul>

      <div className="p-6 bg-slate-800/50 rounded-xl border border-emerald-500/20 my-6">
        <h4 className="text-emerald-400 font-semibold mb-2">Estrategia clave: Prevención de Recaídas de Marlatt</h4>
        <p className="text-slate-300">
          Usada en 90% de centros chilenos: identifica «triggers» (estrés, amigos, lugares) y enseña coping skills. Incluye diario de riesgos + plan emergencia. ¡Reduce recaídas en 50%!
        </p>
      </div>

      <hr className="border-slate-700 my-8" />

      <h2 className="text-emerald-400">2. Costos Reales (Mensuales, Basados en Experiencia 2026)</h2>
      <p>
        Precios varían por comodidades/región, pero aquí promedios reales (incluye extras como medicamentos $50k + transporte $30k/mes en residenciales).
      </p>

      <div className="overflow-x-auto my-6">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-slate-700">
              <th className="p-3 text-emerald-400">Tipo de Tratamiento</th>
              <th className="p-3 text-emerald-400">Costo Mensual</th>
              <th className="p-3 text-emerald-400">Duración Típica</th>
              <th className="p-3 text-emerald-400">Total Estimado</th>
            </tr>
          </thead>
          <tbody className="text-slate-300">
            <tr className="border-b border-slate-800">
              <td className="p-3 font-medium">Gratuito SENDA/FONASA</td>
              <td className="p-3">$0</td>
              <td className="p-3">6-12 meses + seguimiento</td>
              <td className="p-3">$0</td>
            </tr>
            <tr className="border-b border-slate-800">
              <td className="p-3 font-medium">Ambulatorio</td>
              <td className="p-3">$350.000 – $600.000</td>
              <td className="p-3">6-12 meses + seguimiento</td>
              <td className="p-3">$2.100.000 – $7.200.000</td>
            </tr>
            <tr className="border-b border-slate-800">
              <td className="p-3 font-medium">Residencial Estándar</td>
              <td className="p-3">$350.000 – $1.500.000</td>
              <td className="p-3">6-12 meses + seguimiento</td>
              <td className="p-3">$2.100.000 – $18.000.000</td>
            </tr>
            <tr className="border-b border-slate-800">
              <td className="p-3 font-medium">Residencial Premium</td>
              <td className="p-3">$1.500.000 – $3.000.000</td>
              <td className="p-3">6-12 meses + seguimiento</td>
              <td className="p-3">$9.000.000 – $36.000.000</td>
            </tr>
            <tr>
              <td className="p-3 font-medium">Online</td>
              <td className="p-3">$200.000+</td>
              <td className="p-3">6-12 meses + seguimiento</td>
              <td className="p-3">$1.200.000 – $2.400.000</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        <strong>Nota:</strong> Gratuitos existen (SENDA), pero cupos limitados y listas espera. Privados ofrecen más comodidades/flexibilidad. ¡Calcula cuánto ahorras dejando la adicción y cubres todo en <Link to="/calculadora" className="text-emerald-400 hover:underline">nuestra calculadora</Link>!
      </p>

      <hr className="border-slate-700 my-8" />

      <h2 className="text-emerald-400">3. Tiempos Estimados y Estructura Diaria</h2>
      <ul>
        <li><strong>Desintoxicación inicial:</strong> 7-14 días (física, síntomas abstinencia manejados con medicamentos).</li>
        <li><strong>Tratamiento intensivo:</strong> 6-12 meses (terapias individuales y grupales, talleres).</li>
        <li><strong>Seguimiento:</strong> 6-12 meses adicionales (prevención de recaídas, reinserción).</li>
        <li><strong>Mantenimiento:</strong> Post-egreso (grupos semanales, terapia continua 1 año+).</li>
      </ul>

      <h3 className="text-white">Horario típico residencial:</h3>
      <ul>
        <li>7:00 Desayuno + meditación.</li>
        <li>9:00-14:00 Terapias grupales/TCC/Marlatt.</li>
        <li>15:00 Talleres (arte, deporte, familia).</li>
        <li>18:00 Cena + reflexión.</li>
        <li>22:00 Libre/supervisado.</li>
      </ul>
      <p>Ambulatorio: 9-14 hrs, 3-5 días/semana.</p>

      <hr className="border-slate-700 my-8" />

      <h2 className="text-emerald-400">4. Talleres y Actividades que Encontrarás</h2>
      <p>En centros top (busca en <Link to="/centros-de-rehabilitacion-drogas-chile" className="text-emerald-400 hover:underline">nuestro directorio</Link>):</p>
      <ul>
        <li><strong>Prevención Recaídas Marlatt:</strong> Diario triggers + role-play (80% centros).</li>
        <li><strong>Terapia Familiar:</strong> Sesiones semanales (reconstruye lazos).</li>
        <li><strong>Mindfulness/Yoga:</strong> Reduce ansiedad (holísticos).</li>
        <li><strong>Deporte/Arte:</strong> Fútbol, pintura, música (libera dopamina sana).</li>
        <li><strong>Educación:</strong> Charlas nutrición, finanzas, empleo.</li>
        <li><strong>Grupos AA/NA Adaptados:</strong> Compartir experiencias.</li>
      </ul>

      <hr className="border-slate-700 my-8" />

      <h2 className="text-emerald-400">5. Opciones Gratuitas y Cómo Acceder</h2>
      <p>
        Sí existen vía SENDA (1412 o CESFAM), pero foco en calidad/rapidez: privados evitan listas espera. Llama 1412 para info, pero <strong>busca TODAS las opciones (gratuitas + privadas) en <Link to="/centros-de-rehabilitacion-drogas-chile" className="text-emerald-400 hover:underline">nuestro directorio</Link></strong> — filtra por región/precio.
      </p>

      <hr className="border-slate-700 my-8" />

      <h2 className="text-emerald-400">6. Consejos Finales para Elegir</h2>
      <ul>
        <li>Haz nuestros <Link to="/herramientas" className="text-emerald-400 hover:underline">tests</Link> (URICA para motivación, DAST/AUDIT para severidad).</li>
        <li>Prioriza acreditados SENDA + reseñas reales.</li>
        <li>Incluye familia (éxito +40%).</li>
        <li>Evita «milagros» rápidos — recuperación es proceso.</li>
      </ul>

      <div className="p-6 bg-gradient-to-r from-emerald-900/30 to-slate-800/50 rounded-xl border border-emerald-500/20 my-8">
        <h3 className="text-xl font-bold text-white mb-4">¡No esperes más!</h3>
        <p className="text-slate-300 mb-4">El 70% recae por no actuar.</p>
        <div className="flex flex-wrap gap-4">
          <Link 
            to="/asesoria"
            className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg transition-colors"
          >
            Solicitar Asesoría Experta
          </Link>
          <Link 
            to="/centros-de-rehabilitacion-drogas-chile"
            className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-lg transition-colors"
          >
            Buscar tu Centro Ideal
          </Link>
          <Link 
            to="/calculadora"
            className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-lg transition-colors"
          >
            Calcular Ahorro y Tratamiento
          </Link>
        </div>
      </div>

      <p className="text-sm text-slate-500 italic">
        Info actualizada 2026. No sustituye consulta profesional. Llama 1412 urgencias.
      </p>
    </ArticleLayout>
  );
};

export default GuiaTratamientosPage;
